import React, { useState, useEffect, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
 
pdfjs.GlobalWorkerOptions.workerSrc = `//cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs`;
 
const PDFViewer = memo(({ pdfURL, zoom, onLoad }) => (
  <Document
    file={{ url: pdfURL }}
    onLoadSuccess={onLoad}
    onLoadError={(err) => { console.error('PDF load error:', err); onLoad(); }}
  >
    <Page pageNumber={1} scale={zoom} />
  </Document>
));
 
// Common tolerances — most used first
const TOLERANCE_OPTIONS = [
  '±.005', '±.010', '±.001', '±.002', '±.003',
  '±.015', '±.020', '±.030', '±.050', '±.100',
  '+.000/-.001', '+.000/-.002', '+.000/-.005',
  'REF', 'Basic', '—'
];
 
// Common requirements quick-fill
const REQUIREMENT_OPTIONS = [
  'OAL', 'Diameter', 'Radius', 'Thread', 'Bore',
  'Depth', 'Width', 'Height', 'Chamfer', 'Surface Finish',
  'True Position', 'Flatness', 'Perpendicularity', 'Parallelism',
  'Runout', 'Concentricity', 'Angularity', 'Hole Diameter',
  'Wall Thickness', 'Shoulder Diameter', 'Thread Relief'
];
 
function ReviewBubbles() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [bubbles, setBubbles] = useState([]);
  const [selectedBubble, setSelectedBubble] = useState(null);
  const [tool, setTool] = useState('select');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1.4);
  const [movingBubble, setMovingBubble] = useState(null);
  const [movingAnchor, setMovingAnchor] = useState(null);
  const [customTolerance, setCustomTolerance] = useState(false);
 
  useEffect(() => {
    const loadProject = async () => {
      try {
        const docRef = doc(db, 'projects', projectId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProject(data);
          setBubbles(data.bubbles || []);
        }
      } catch (err) {
        console.error('Error loading project:', err);
      }
    };
    loadProject();
  }, [projectId]);
 
  const handlePdfLoad = useCallback(() => setLoading(false), []);
 
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'b' || e.key === 'B') setTool('bubble');
      if (e.key === 's' || e.key === 'S') setTool('select');
      if (e.key === 'Escape') {
        setSelectedBubble(null);
        setMovingBubble(null);
        setMovingAnchor(null);
        setTool('select');
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedBubble) {
        setBubbles(prev => prev.filter(b => b.id !== selectedBubble).map((b, i) => ({ ...b, number: i + 1 })));
        setSelectedBubble(null);
        setMovingBubble(null);
        setMovingAnchor(null);
      }
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    const handleWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoom(prev => e.deltaY < 0
        ? Math.min(3, parseFloat((prev + 0.1).toFixed(1)))
        : Math.max(0.4, parseFloat((prev - 0.1).toFixed(1)))
      );
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [selectedBubble, bubbles, zoom]);
 
  const handleCanvasClick = (e) => {
    if (movingBubble) { setMovingBubble(null); return; }
    if (movingAnchor) { setMovingAnchor(null); return; }
    if (tool !== 'bubble') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const newBubble = {
      id: Date.now().toString(),
      number: bubbles.length + 1,
      x: x - 6, y: y - 6,
      ax: x, ay: y,
      requirement: '', nominal: '', tolerance: '',
      featureType: 'Linear', inspectionMethod: 'Caliper', status: 'Pending'
    };
    setBubbles([...bubbles, newBubble]);
    setSelectedBubble(newBubble.id);
    setCustomTolerance(false);
    setTool('select');
  };
 
  const handleMouseMove = (e) => {
    if (!movingBubble && !movingAnchor) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const cx = Math.max(0, Math.min(100, x));
    const cy = Math.max(0, Math.min(100, y));
    if (movingBubble) setBubbles(prev => prev.map(b => b.id === movingBubble ? { ...b, x: cx, y: cy } : b));
    if (movingAnchor) setBubbles(prev => prev.map(b => b.id === movingAnchor ? { ...b, ax: cx, ay: cy } : b));
  };
 
  const handleBubbleClick = (e, bubbleId) => {
    e.stopPropagation();
    if (movingBubble === bubbleId) { setMovingBubble(null); return; }
    if (movingAnchor) { setMovingAnchor(null); return; }
    setSelectedBubble(bubbleId);
    setCustomTolerance(false);
  };
 
  const handleBubbleDoubleClick = (e, bubbleId) => {
    e.stopPropagation();
    setMovingBubble(bubbleId);
    setSelectedBubble(bubbleId);
  };
 
  const handleAnchorDoubleClick = (e, bubbleId) => {
    e.stopPropagation();
    setMovingAnchor(bubbleId);
    setSelectedBubble(bubbleId);
  };
 
  const updateBubble = (id, field, value) => {
    setBubbles(prev => prev.map(b => b.id === id ? { ...b, [field]: value } : b));
  };
 
  const deleteBubble = (id) => {
    setBubbles(prev => prev.filter(b => b.id !== id).map((b, i) => ({ ...b, number: i + 1 })));
    setSelectedBubble(null);
    setMovingBubble(null);
    setMovingAnchor(null);
  };
 
  // Navigate to next bubble
  const goToNextBubble = () => {
    if (!selectedBubble) return;
    const idx = bubbles.findIndex(b => b.id === selectedBubble);
    if (idx < bubbles.length - 1) {
      setSelectedBubble(bubbles[idx + 1].id);
      setCustomTolerance(false);
    }
  };
 
  // Navigate to previous bubble
  const goToPrevBubble = () => {
    if (!selectedBubble) return;
    const idx = bubbles.findIndex(b => b.id === selectedBubble);
    if (idx > 0) {
      setSelectedBubble(bubbles[idx - 1].id);
      setCustomTolerance(false);
    }
  };
 
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'projects', projectId), { bubbles });
      alert('Saved successfully!');
    } catch (err) {
      alert('Error saving. Please try again.');
    }
    setSaving(false);
  };
 
  const selected = bubbles.find(b => b.id === selectedBubble);
  const selectedIdx = bubbles.findIndex(b => b.id === selectedBubble);
  const canvasCursor = movingBubble || movingAnchor ? 'crosshair' : tool === 'bubble' ? 'crosshair' : 'default';
 
  // Stats for header
  const passCount = bubbles.filter(b => b.status === 'Pass').length;
  const failCount = bubbles.filter(b => b.status === 'Fail').length;
  const pendingCount = bubbles.filter(b => b.status === 'Pending').length;
 
  return (
    <div style={styles.page}>
      {/* SIDEBAR */}
      <div style={styles.sidebar}>
        <div style={styles.logo}>
          <span style={styles.logoText}>M<span style={styles.logoBlue}>I</span>A</span>
          <div style={styles.logoSub}>Manufacturing<br />Inspection Assistant</div>
        </div>
        <nav style={styles.nav}>
          <div style={styles.navItem} onClick={() => navigate('/')}><span>⬆</span> Upload Print</div>
          <div style={{ ...styles.navItem, ...styles.navActive }}>
            <span>◎</span> Review Bubbles
            {bubbles.length > 0 && <span style={styles.navBadge}>{bubbles.length}</span>}
          </div>
          <div style={styles.navItem} onClick={() => navigate(`/fai/${projectId}`)}><span>☰</span> FAI Document</div>
          <div style={styles.navItem} onClick={() => navigate(`/final/${projectId}`)}><span>✓</span> Final Inspection</div>
          <div style={styles.navItem} onClick={() => navigate(`/exports/${projectId}`)}><span>↓</span> Exports</div>
        </nav>
        <div style={styles.shortcutHint}>
          <div style={styles.shortcutTitle}>Shortcuts</div>
          <div style={styles.shortcutItem}><span style={styles.key}>B</span> Add bubble</div>
          <div style={styles.shortcutItem}><span style={styles.key}>S</span> Select</div>
          <div style={styles.shortcutItem}><span style={styles.key}>Del</span> Delete</div>
          <div style={styles.shortcutItem}><span style={styles.key}>Esc</span> Deselect</div>
          <div style={styles.shortcutItem}><span style={styles.key}>⌃S</span> Save</div>
          <div style={styles.shortcutItem}><span style={styles.key}>⌃scroll</span> Zoom</div>
        </div>
      </div>
 
      {/* MAIN */}
      <div style={styles.main}>
        <div style={styles.topbar}>
          <div style={styles.stepperRow}>
            <div style={styles.stepDone}>✓ Upload PDF</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepDone}>✓ Analyze Print</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepActive}>3 Review Bubbles</div>
            <div style={styles.stepLineIdle}></div>
            <div style={styles.stepIdle}>4 Generate FAI</div>
            <div style={styles.stepLineIdle}></div>
            <div style={styles.stepIdle}>5 Final Inspection</div>
          </div>
          <div style={styles.topbarRight}>
            <button style={styles.saveBtnOutline} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button style={styles.approveBtn} onClick={() => navigate(`/fai/${projectId}`)}>Approve Bubbles →</button>
          </div>
        </div>
 
        <div style={styles.aiBar}>
          <div style={styles.metaRow}>
            {project && <>
              <span style={styles.metaItem}><span style={styles.metaLabel}>Part</span> {project.partNumber}</span>
              <span style={styles.metaItem}><span style={styles.metaLabel}>Rev</span> {project.revision || '—'}</span>
              <span style={styles.metaItem}><span style={styles.metaLabel}>Material</span> {project.material || '—'}</span>
              <span style={styles.metaItem}><span style={styles.metaLabel}>Units</span> {project.units}</span>
            </>}
          </div>
        </div>
 
        <div style={styles.workspace}>
          <div style={styles.pdfCol}>
            <div style={styles.pdfToolbar}>
              <div style={styles.fileChip}>📄 {project?.fileName || 'Loading...'}</div>
              <div style={styles.toolGroup}>
                <button style={{ ...styles.toolBtn, ...(tool === 'select' ? styles.toolBtnActive : {}) }} onClick={() => setTool('select')}>↖ Select</button>
                <button style={{ ...styles.toolBtn, ...(tool === 'bubble' ? styles.toolBtnBubble : {}) }} onClick={() => setTool('bubble')}>⊕ Add Bubble</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                <button style={styles.toolBtn} onClick={() => setZoom(Math.max(0.4, zoom - 0.2))}>−</button>
                <span style={{ fontSize: '12px', color: '#374151', fontWeight: '600', minWidth: '48px', textAlign: 'center', fontFamily: 'monospace' }}>{Math.round(zoom * 100)}%</span>
                <button style={styles.toolBtn} onClick={() => setZoom(Math.min(3, zoom + 0.2))}>+</button>
                <button style={styles.toolBtn} onClick={() => setZoom(1.4)}>Fit</button>
              </div>
              {tool === 'bubble' && <div style={styles.toolHint}>Click on the dimension to place a bubble + leader</div>}
              {movingBubble && <div style={{ ...styles.toolHint, color: '#dc2626' }}>Moving bubble — click to drop</div>}
              {movingAnchor && <div style={{ ...styles.toolHint, color: '#7c3aed' }}>Moving anchor — click to drop on dimension</div>}
            </div>
 
            <div style={styles.canvasWrap}>
              {loading && (
                <div style={styles.loadingOverlay}>
                  <div style={styles.loadingText}>Loading drawing...</div>
                </div>
              )}
              <div
                style={{ ...styles.canvasContainer, cursor: canvasCursor }}
                onClick={handleCanvasClick}
                onMouseMove={handleMouseMove}
              >
                {project?.pdfURL && <PDFViewer pdfURL={project.pdfURL} zoom={zoom} onLoad={handlePdfLoad} />}
 
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 3 }}>
                  {bubbles.map(bubble => {
                    const hasAnchor = bubble.ax !== undefined && bubble.ay !== undefined;
                    if (!hasAnchor) return null;
                    const isSelected = selectedBubble === bubble.id;
                    const color = bubble.status === 'Pass' ? '#16a34a' : bubble.status === 'Fail' ? '#dc2626' : '#2563EB';
                    return (
                      <line key={`line-${bubble.id}`}
                        x1={`${bubble.x}%`} y1={`${bubble.y}%`}
                        x2={`${bubble.ax}%`} y2={`${bubble.ay}%`}
                        stroke={color}
                        strokeWidth={isSelected ? 1.8 : 1.2}
                        strokeOpacity={isSelected ? 0.9 : 0.6}
                      />
                    );
                  })}
                </svg>
 
                {bubbles.map(bubble => {
                  const hasAnchor = bubble.ax !== undefined && bubble.ay !== undefined;
                  if (!hasAnchor) return null;
                  const isSelected = selectedBubble === bubble.id;
                  const color = bubble.status === 'Pass' ? '#16a34a' : bubble.status === 'Fail' ? '#dc2626' : '#2563EB';
                  return (
                    <div key={`anchor-${bubble.id}`} style={{
                      position: 'absolute', left: `${bubble.ax}%`, top: `${bubble.ay}%`,
                      width: isSelected ? '10px' : '8px', height: isSelected ? '10px' : '8px',
                      borderRadius: '50%', background: color, border: '2px solid #fff',
                      transform: 'translate(-50%, -50%)', zIndex: 4, cursor: 'pointer',
                      boxShadow: isSelected ? `0 0 0 3px ${color}44` : '0 1px 3px rgba(0,0,0,0.3)',
                      transition: 'all 0.15s ease'
                    }}
                      onDoubleClick={(e) => handleAnchorDoubleClick(e, bubble.id)}
                      onClick={(e) => { e.stopPropagation(); if (movingAnchor) { setMovingAnchor(null); return; } setSelectedBubble(bubble.id); }}
                      title="Double-click to move anchor"
                    />
                  );
                })}
 
                {bubbles.map(bubble => (
                  <div key={bubble.id} style={{
                    ...styles.bubble,
                    left: `${bubble.x}%`, top: `${bubble.y}%`,
                    background: bubble.status === 'Pass' ? '#16a34a' : bubble.status === 'Fail' ? '#dc2626' : '#2563EB',
                    boxShadow: movingBubble === bubble.id
                      ? '0 0 0 4px rgba(220,38,38,0.4), 0 6px 16px rgba(0,0,0,0.4)'
                      : selectedBubble === bubble.id
                        ? '0 0 0 4px rgba(37,99,235,0.3), 0 4px 12px rgba(0,0,0,0.3)'
                        : '0 2px 8px rgba(0,0,0,0.3)',
                    transform: movingBubble === bubble.id
                      ? 'translate(-50%, -50%) scale(1.25)'
                      : selectedBubble === bubble.id
                        ? 'translate(-50%, -50%) scale(1.15)'
                        : 'translate(-50%, -50%)',
                    cursor: movingBubble === bubble.id ? 'grabbing' : 'pointer'
                  }}
                    onClick={(e) => handleBubbleClick(e, bubble.id)}
                    onDoubleClick={(e) => handleBubbleDoubleClick(e, bubble.id)}
                    title="Click to select · Double-click to move"
                  >
                    {bubble.number}
                  </div>
                ))}
              </div>
            </div>
 
            <div style={styles.miaBanner}>
              <div style={styles.miaLeft}>
                <div style={styles.miaIcon}>✦</div>
                <div style={styles.miaText}>
                  {bubbles.length === 0
                    ? 'Press B then click a dimension to place a bubble with leader line.'
                    : `${bubbles.length} bubble${bubbles.length > 1 ? 's' : ''} · Double-click bubble to move · Double-click anchor to reposition`}
                </div>
              </div>
            </div>
          </div>
 
          {/* RIGHT PANEL */}
          <div style={styles.rightPanel}>
 
            {/* PANEL HEADER WITH STATS */}
            <div style={styles.rpHeader}>
              <div style={styles.rpTitle}>Bubbles ({bubbles.length})</div>
              {bubbles.length > 0 && (
                <div style={styles.rpStats}>
                  <span style={{ color: '#16a34a', fontWeight: '600', fontSize: '11px' }}>{passCount}✓</span>
                  <span style={{ color: '#dc2626', fontWeight: '600', fontSize: '11px' }}>{failCount}✕</span>
                  <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '11px' }}>{pendingCount}⏳</span>
                </div>
              )}
            </div>
 
            {/* BUBBLE EDITOR */}
            {selected ? (
              <div style={styles.bubbleEditor}>
                {/* Editor header with prev/next nav */}
                <div style={styles.editorHeader}>
                  <button
                    style={{ ...styles.navArrowBtn, opacity: selectedIdx === 0 ? 0.3 : 1 }}
                    onClick={goToPrevBubble}
                    disabled={selectedIdx === 0}
                    title="Previous bubble"
                  >←</button>
                  <div style={styles.editorBubble}>{selected.number}</div>
                  <div style={styles.editorTitle}>Bubble #{selected.number} <span style={{ fontSize: '10px', color: '#9ca3af', fontWeight: '400' }}>of {bubbles.length}</span></div>
                  <button
                    style={{ ...styles.navArrowBtn, opacity: selectedIdx === bubbles.length - 1 ? 0.3 : 1 }}
                    onClick={goToNextBubble}
                    disabled={selectedIdx === bubbles.length - 1}
                    title="Next bubble"
                  >→</button>
                  <button style={styles.deleteBtn} onClick={() => deleteBubble(selected.id)} title="Delete (Del)">🗑</button>
                </div>
 
                <div style={styles.editorFields}>
 
                  {/* REQUIREMENT with quick-fill dropdown */}
                  <div style={styles.fieldGroup}>
                    <label style={styles.fieldLabel}>Requirement</label>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <select
                        style={{ ...styles.fieldInput, width: '130px', flexShrink: 0, color: '#9ca3af' }}
                        value=""
                        onChange={e => {
                          if (e.target.value) updateBubble(selected.id, 'requirement', e.target.value);
                        }}
                      >
                        <option value="">Quick fill...</option>
                        {REQUIREMENT_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <input
                        style={{ ...styles.fieldInput, flex: 1 }}
                        placeholder="or type custom"
                        value={selected.requirement}
                        onChange={e => updateBubble(selected.id, 'requirement', e.target.value)}
                      />
                    </div>
                  </div>
 
                  <div style={styles.fieldRow}>
                    <div style={styles.fieldGroup}>
                      <label style={styles.fieldLabel}>Nominal</label>
                      <input style={styles.fieldInput} placeholder="4.250" value={selected.nominal} onChange={e => updateBubble(selected.id, 'nominal', e.target.value)} />
                    </div>
 
                    {/* TOLERANCE with dropdown + custom */}
                    <div style={styles.fieldGroup}>
                      <label style={styles.fieldLabel}>
                        Tolerance
                        <span
                          style={{ marginLeft: '6px', fontSize: '9px', color: '#2563EB', cursor: 'pointer', fontWeight: '600' }}
                          onClick={() => setCustomTolerance(!customTolerance)}
                        >
                          {customTolerance ? '▲ list' : '✎ custom'}
                        </span>
                      </label>
                      {customTolerance ? (
                        <input
                          style={styles.fieldInput}
                          placeholder="e.g. +.001/-.000"
                          value={selected.tolerance}
                          onChange={e => updateBubble(selected.id, 'tolerance', e.target.value)}
                          autoFocus
                        />
                      ) : (
                        <select
                          style={styles.fieldInput}
                          value={selected.tolerance}
                          onChange={e => updateBubble(selected.id, 'tolerance', e.target.value)}
                        >
                          <option value="">Select...</option>
                          {TOLERANCE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
 
                  <div style={styles.fieldRow}>
                    <div style={styles.fieldGroup}>
                      <label style={styles.fieldLabel}>Feature Type</label>
                      <select style={styles.fieldInput} value={selected.featureType} onChange={e => updateBubble(selected.id, 'featureType', e.target.value)}>
                        <option>Linear</option><option>Diameter</option><option>Radius</option>
                        <option>GD&T</option><option>Datum</option><option>Angle</option>
                        <option>Thread</option><option>Surface Finish</option><option>Note</option>
                      </select>
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.fieldLabel}>Inspection Method</label>
                      <select style={styles.fieldInput} value={selected.inspectionMethod} onChange={e => updateBubble(selected.id, 'inspectionMethod', e.target.value)}>
                        <option>Caliper</option><option>Micrometer</option><option>CMM</option>
                        <option>Visual</option><option>Go/No-Go</option><option>Optical Comparator</option>
                        <option>Height Gauge</option><option>Protractor</option><option>Thread Gauge</option>
                        <option>Surface Profilometer</option>
                      </select>
                    </div>
                  </div>
 
                  <div style={styles.fieldGroup}>
                    <label style={styles.fieldLabel}>Status</label>
                    <select
                      style={{ ...styles.fieldInput, color: selected.status === 'Pass' ? '#16a34a' : selected.status === 'Fail' ? '#dc2626' : '#374151' }}
                      value={selected.status}
                      onChange={e => updateBubble(selected.id, 'status', e.target.value)}
                    >
                      <option>Pending</option><option>Pass</option><option>Fail</option><option>N/A</option>
                    </select>
                  </div>
 
                  <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                    <button style={styles.moveBtn} onClick={() => setMovingBubble(movingBubble === selected.id ? null : selected.id)}>
                      {movingBubble === selected.id ? '✕ Cancel' : '✥ Move Bubble'}
                    </button>
                    <button style={{ ...styles.moveBtn, color: '#7c3aed', borderColor: '#e9d5ff' }} onClick={() => setMovingAnchor(movingAnchor === selected.id ? null : selected.id)}>
                      {movingAnchor === selected.id ? '✕ Cancel' : '⊙ Move Anchor'}
                    </button>
                  </div>
 
                  {/* Next bubble button */}
                  {selectedIdx < bubbles.length - 1 && (
                    <button style={styles.nextBubbleBtn} onClick={goToNextBubble}>
                      Next Bubble ({selected.number + 1} of {bubbles.length}) →
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div style={styles.emptyEditor}>
                Click a bubble on the drawing or in the list below to edit it
              </div>
            )}
 
            {/* INSPECTION TABLE */}
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Requirement</th>
                    <th style={styles.th}>Nominal</th>
                    <th style={styles.th}>Tol</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bubbles.length === 0 ? (
                    <tr><td colSpan={5} style={styles.emptyRow}>No bubbles yet — press B or use Add Bubble</td></tr>
                  ) : (
                    bubbles.map(b => (
                      <tr key={b.id}
                        style={{ ...styles.tr, background: selectedBubble === b.id ? '#eff6ff' : 'transparent' }}
                        onClick={() => { setSelectedBubble(b.id); setCustomTolerance(false); }}
                      >
                        <td style={styles.td}>
                          <div style={{
                            ...styles.bubbleNum,
                            background: b.status === 'Pass' ? '#16a34a' : b.status === 'Fail' ? '#dc2626' : '#2563EB'
                          }}>{b.number}</div>
                        </td>
                        <td style={styles.td}>{b.requirement || <span style={styles.empty}>—</span>}</td>
                        <td style={{ ...styles.td, fontFamily: 'monospace' }}>{b.nominal || <span style={styles.empty}>—</span>}</td>
                        <td style={{ ...styles.td, fontFamily: 'monospace' }}>{b.tolerance || <span style={styles.empty}>—</span>}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.statusChip,
                            background: b.status === 'Pass' ? '#dcfce7' : b.status === 'Fail' ? '#fef2f2' : '#f3f4f6',
                            color: b.status === 'Pass' ? '#16a34a' : b.status === 'Fail' ? '#dc2626' : '#6b7280'
                          }}>
                            {b.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
 
            <div style={styles.rpFooter}>
              <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : '💾 Save Progress'}</button>
              <button style={styles.nextBtn} onClick={() => navigate(`/fai/${projectId}`)}>Generate FAI →</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
 
const styles = {
  page: { display: 'flex', height: '100vh', background: '#f0f2f5', fontFamily: 'Inter, sans-serif', overflow: 'hidden' },
  sidebar: { width: '210px', background: '#0d1b2a', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  logo: { padding: '24px 18px 20px', borderBottom: '1px solid #1a2a3a', display: 'flex', flexDirection: 'column', gap: '4px' },
  logoText: { fontSize: '32px', fontWeight: '800', color: '#fff', letterSpacing: '-1px', lineHeight: 1 },
  logoBlue: { color: '#3b82f6' },
  logoSub: { fontSize: '10px', color: '#475569', lineHeight: '1.4', marginTop: '4px' },
  nav: { padding: '12px 0', flex: 1 },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 18px', fontSize: '13px', color: '#64748b', cursor: 'pointer' },
  navActive: { color: '#e2e8f0', fontWeight: '500', borderLeft: '3px solid #3b82f6', paddingLeft: '15px' },
  navBadge: { background: '#2563EB', color: '#fff', fontSize: '10px', fontWeight: '600', padding: '1px 7px', borderRadius: '10px', marginLeft: 'auto' },
  shortcutHint: { padding: '12px 16px 16px', borderTop: '1px solid #1a2a3a' },
  shortcutTitle: { fontSize: '10px', fontWeight: '600', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' },
  shortcutItem: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', fontSize: '11px', color: '#64748b' },
  key: { background: '#1e2d3d', border: '1px solid #334155', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', color: '#94a3b8', fontFamily: 'monospace', flexShrink: 0 },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  topbar: { height: '52px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', padding: '0 20px', flexShrink: 0 },
  stepperRow: { display: 'flex', alignItems: 'center', gap: '0', flex: 1 },
  stepDone: { fontSize: '12px', fontWeight: '500', color: '#2563EB', whiteSpace: 'nowrap' },
  stepActive: { fontSize: '12px', fontWeight: '600', color: '#111827', whiteSpace: 'nowrap' },
  stepIdle: { fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' },
  stepLine: { width: '40px', height: '1.5px', background: '#2563EB', margin: '0 6px' },
  stepLineIdle: { width: '40px', height: '1.5px', background: '#e5e7eb', margin: '0 6px' },
  topbarRight: { display: 'flex', gap: '8px' },
  saveBtnOutline: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '7px', padding: '7px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', color: '#374151' },
  approveBtn: { background: '#2563EB', border: 'none', borderRadius: '7px', padding: '7px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', color: '#fff' },
  aiBar: { height: '40px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', padding: '0 20px', gap: '20px', flexShrink: 0 },
  aiChip: { fontSize: '12px', color: '#16a34a', fontWeight: '500', whiteSpace: 'nowrap' },
  metaRow: { display: 'flex', gap: '20px' },
  metaItem: { fontSize: '12px', color: '#374151' },
  metaLabel: { fontSize: '10px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', marginRight: '4px' },
  workspace: { flex: 1, display: 'flex', overflow: 'hidden' },
  pdfCol: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  pdfToolbar: { height: '40px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', padding: '0 12px', gap: '8px', flexShrink: 0 },
  fileChip: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px 10px', fontSize: '11.5px', color: '#374151', fontFamily: 'monospace' },
  toolGroup: { display: 'flex', gap: '4px' },
  toolBtn: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 12px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', color: '#374151' },
  toolBtnActive: { background: '#eff6ff', borderColor: '#bfdbfe', color: '#2563EB' },
  toolBtnBubble: { background: '#2563EB', borderColor: '#2563EB', color: '#fff' },
  toolHint: { fontSize: '11.5px', color: '#2563EB', fontWeight: '500' },
  canvasWrap: { flex: 1, background: '#6b7280', overflow: 'auto', position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', padding: '20px' },
  loadingOverlay: { position: 'absolute', inset: 0, background: 'rgba(107,114,128,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 },
  loadingText: { color: '#fff', fontSize: '14px', fontWeight: '500' },
  canvasContainer: { position: 'relative', display: 'inline-block', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' },
  bubble: { position: 'absolute', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: '#fff', cursor: 'pointer', transform: 'translate(-50%, -50%)', userSelect: 'none', zIndex: 5, fontFamily: 'monospace', border: '2px solid #fff', transition: 'box-shadow 0.15s ease, transform 0.15s ease' },
  miaBanner: { height: '44px', background: '#eff6ff', borderTop: '1px solid #dbeafe', display: 'flex', alignItems: 'center', padding: '0 16px', flexShrink: 0 },
  miaLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  miaIcon: { width: '28px', height: '28px', background: '#2563EB', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', flexShrink: 0 },
  miaText: { fontSize: '12.5px', color: '#1e40af' },
  rightPanel: { width: '380px', background: '#fff', borderLeft: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' },
  rpHeader: { height: '44px', padding: '0 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
  rpTitle: { fontSize: '14px', fontWeight: '700', color: '#111827' },
  rpStats: { display: 'flex', gap: '8px' },
  bubbleEditor: { background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '12px 14px', flexShrink: 0 },
  emptyEditor: { padding: '16px', fontSize: '12px', color: '#9ca3af', textAlign: 'center', borderBottom: '1px solid #e5e7eb', flexShrink: 0 },
  editorHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' },
  navArrowBtn: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '5px', padding: '3px 8px', fontSize: '13px', cursor: 'pointer', color: '#374151', fontFamily: 'inherit', flexShrink: 0 },
  editorBubble: { width: '26px', height: '26px', borderRadius: '50%', background: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: '#fff', flexShrink: 0 },
  editorTitle: { fontSize: '13px', fontWeight: '600', color: '#111827', flex: 1 },
  deleteBtn: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '15px', padding: '2px' },
  editorFields: { display: 'flex', flexDirection: 'column', gap: '7px' },
  fieldRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '3px' },
  fieldLabel: { fontSize: '10px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' },
  fieldInput: { padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', color: '#111827', fontFamily: 'inherit', outline: 'none', background: '#fff' },
  moveBtn: { flex: 1, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 10px', fontSize: '11.5px', fontWeight: '600', color: '#374151', cursor: 'pointer', fontFamily: 'inherit' },
  nextBubbleBtn: { width: '100%', background: '#2563EB', border: 'none', borderRadius: '6px', padding: '8px', fontSize: '12px', fontWeight: '600', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', marginTop: '2px' },
  tableWrap: { flex: 1, overflowY: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { background: '#f9fafb', padding: '8px 12px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #f3f4f6', position: 'sticky', top: 0 },
  tr: { cursor: 'pointer', borderBottom: '1px solid #f9fafb' },
  td: { padding: '7px 12px', fontSize: '12px', color: '#374151', verticalAlign: 'middle' },
  bubbleNum: { width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: '#fff' },
  statusChip: { fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px' },
  emptyRow: { padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' },
  empty: { color: '#d1d5db' },
  rpFooter: { padding: '12px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '8px', flexShrink: 0 },
  saveBtn: { flex: 1, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', color: '#374151' },
  nextBtn: { flex: 1, background: '#2563EB', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', color: '#fff' },
};
 
export default ReviewBubbles;